import { Box, Drawer, Button, Chip, CircularProgress, Collapse, FormControl, FormHelperText, TextField, Typography, Paper, Select, MenuItem, useTheme, Popover, IconButton } from '@mui/material'
import React, { useCallback, useEffect, useState } from 'react'
import { Formik, Form, Field, ErrorMessage } from 'formik'
import * as Yup from 'yup'
import FileInput from '../../components/study-material-components/FileInput'
import BreadcrumbList from '../../components/common/BreadcrumbList'
import { toast } from 'react-toastify'
import { useNavigate } from 'react-router-dom'
import { useStudyMaterialStore } from '../../store/studymaterial.store'
import { useUserStore } from '../../store/user.store'
import { Icons, FLAT_COLORS, convertToCDNLink, attachmentThumbnails, getFileSizeFromBytes } from '../../utils/utilities'
import DocumentViewer from '../../components/common/DocumentViewer'
import { useSettingsStore } from '../../store/settings.store'
import { Download, Visibility } from '@mui/icons-material'
import InfoIcon from '@mui/icons-material/Info'
import DataLoadingSpinner from '../../components/common/DataLoadingSpinner'
import CustomSelectField from '../../components/common/CustomSelectField'
import CustomChip from '../../components/common/CustomChip'

const subjectFilterFunc = ( sub, values ) => {

    if ( sub.ac_year_id !== values.academicYear ) return false

    return true
}

const AddMaterial = () => {

    const [tags, setTags] = useState( [] )
    const [selectedAttachments, setSelectedAttachments] = useState( [] )
    const [isAddingMaterial, setIsAddingMaterial] = useState( false )
    const [isLoadingSubjects, setIsLoadingSubjects] = useState( true )
    const [availableSubjects, setAvailableSubjects] = useState( [] )
    const [attachmentsNames, setAttachmentsNames] = useState( [] )
    const [attachmentsSizes, setAttachmentsSizes] = useState( [] )
    const [isDialogOpen, setIsDialogOpen] = useState( false )
    const [documentType, setDocumentType] = useState( null )
    const [documentUrl, setDocumentUrl] = useState( null )
    const [formatFiles, setFormatFiles] = useState( [] )
    const [refreshing, setRefreshing] = useState( null )
    const [filename, setfilename] = useState( null )
    const [anchorEl, setAnchorEl] = useState( null )
    const [progress, setProgress] = useState( 0 )

    const navigate = useNavigate()

    const StudyMaterialStore = useStudyMaterialStore()
    const SettingsStore = useSettingsStore()
    const UserStore = useUserStore()

    const { palette, border } = useTheme()

    const initialValues = {
        materialName: '',
        materialDesc: '',
        tags: '',
        subject: '',
        attachments: null,
        academicYear: UserStore.getUserAcademicYears[0]?.ac_year_id,
        year: null,
        sem: null
    }

    const validationSchema = Yup.object().shape( {
        materialName: Yup.string().required( "Material name is required" ),
        subject: Yup.string().required( "Subject is required" ).notOneOf( ["-"], "Please select related subject" ),
        tags: Yup.mixed().required( "Tags is required" ).test( "TAGS", "Please add atleast one tag", ( val ) => {
            return val && val.length > 0
        } )
    } )

    const deleteTag = ( index ) => {
        const t = [...tags]
        t.splice( index, 1 )
        setTags( t )
    }

    const handleKeyDown = ( e, setFieldValue ) => {
        if ( e.code === "Enter" ) {
            e.preventDefault()
            if ( e.target.value.trim() !== '' ) {
                let newTags = e.target.value.split( "," )
                let filteredTags = []
                newTags.forEach( tag => {
                    if ( tag.trim() !== '' ) filteredTags.push( tag )
                } )
                setTags( [...tags, ...filteredTags] )
                setFieldValue( 'tags', [...tags, ...filteredTags].join( "," ) )
                e.target.value = ""
            }
        }
    }

    const progressHandler = ( event ) => {
        const { loaded, total } = event
        let percent = Math.round( ( loaded * 100 ) / total )
        console.log( percent )
        if ( percent <= 100 ) {
            setProgress( percent )
        }
    }

    const handleAcademicYearChange = ( event, setFieldValue ) => {
        setFieldValue( 'academicYear', event.target.value )
        setFieldValue( 'subject', null )
        setFieldValue( 'year', '' )
        setFieldValue( 'sem', '' )
    }

    const handleBlur = ( e, setFieldValue ) => {
        if ( e.target.value.trim() !== '' ) {
            let newTags = e.target.value.split( "," )
            let filteredTags = []
            newTags.forEach( tag => {
                if ( tag.trim() !== '' ) filteredTags.push( tag )
            } )
            setTags( [...tags, ...filteredTags] )
            setFieldValue( 'tags', [...tags, ...filteredTags].join( "," ) )
            e.target.value = ""
        }
    }

    const addMaterial = async ( values, { resetForm } ) => {
        setIsAddingMaterial( true )
        try {
            const formData = new FormData()
            formData.append( 'name', values.materialName )
            formData.append( 'description', values.materialDesc )
            formData.append( 'tags', values.tags )
            formData.append( 'subject', JSON.stringify( ( UserStore.getUserSubjectsDetails?.currentSubjects || [] ).find( sub => sub.subject_id === values.subject ) ) )
            let emptyName = false
            for ( let i = 0; i < attachmentsNames.length; i++ ) {
                const name = attachmentsNames[i]
                if ( name === "" ) {
                    emptyName = true
                }
            }
            if ( !emptyName ) {
                if ( values.attachments ) {
                    values.attachments.forEach( ( file, i ) => {
                        let fileName = encodeURIComponent( `${file.name}`.replace( /\s/g, '_' ) )
                        formData.append( 'attachments[]', file, fileName )
                    } )
                }
                formData.append( 'attachmentNames[]', JSON.stringify( attachmentsNames ) )
                formData.append( 'attachmentsSizes[]', JSON.stringify( attachmentsSizes ) )
                if ( await StudyMaterialStore.createMaterial( formData, progressHandler ) )
                    navigate( '/faculty/material' )
            } else {
                toast( "Please provide names for all the attachments" )
            }
        } catch ( e ) {
            console.log( e )
            if ( e?.response?.data?.data?.unuploaded?.length > 0 ) {
                setSelectedAttachments( [] )
                setAttachmentsNames( [] )
                let unuploadedFiles = []
                for ( let i = 0; i < e?.response?.data?.data?.unuploaded.length; i++ ) {
                    const file = e?.response?.data?.data?.unuploaded[i]
                    unuploadedFiles.push( file.originalname.replace( /-(\d)+/g, "" ) )
                }
                const toastMsg = <p>Failed to upload these files: <b><i>{unuploadedFiles.join( ", " )}</i></b> due to unsupported format. </p>
                toast( toastMsg )
            } else
                toast.error( e.response && e.response.data && e.response.data.message )
        } finally {
            setIsAddingMaterial( false )
        }
    }

    const handleOpenPopover = ( event ) => {
        const fileUrlString = SettingsStore.getUseSettings['sample_study_material_format']
        if ( fileUrlString ) {
            const parsedFiles = JSON.parse( fileUrlString )
            setFormatFiles( parsedFiles )
            setAnchorEl( event.currentTarget )
        } else {
            console.error( 'Cannot Retrieve Files' )
        }
    }

    const handleClosePopover = () => {
        setAnchorEl( null )
    }

    const handleViewFile = ( file ) => {
        const cdnLink = convertToCDNLink( file.location )
        setDocumentUrl( cdnLink )
        setDocumentType( file.name.split( '.' ).pop() )
        setfilename( file.name )
        setIsDialogOpen( true )
    }

    const handleDownloadFile = ( file ) => {
        const link = document.createElement( 'a' )
        link.href = file.location
        link.download = file.name
        link.click()
        link.remove()
    }

    const handleCloseDialog = () => {
        setIsDialogOpen( false )
        setDocumentUrl( null )
        setDocumentType( null )
    }

    const refreshYears = async () => {
        setRefreshing( "years" )
        await UserStore.fetchUserAcademicYears( true )
        setRefreshing( null )
    }

    const getSubjects = useCallback( async () => {
        setIsLoadingSubjects( true )
        if ( UserStore.getErrorsStatuses?.subjects === true )
            await UserStore.fetchUserSubjects()
        if ( !UserStore.getErrorsStatuses?.subjects ) {
            await StudyMaterialStore.fetchMaterials()
            const facultyMaterials = StudyMaterialStore.getMaterials["materials"]
            let availableSubjects = []
            const uniqueSubjects = {}
            for ( let i = 0; i < ( UserStore.getUserSubjectsDetails?.currentSubjects || [] ).length; i++ ) {
                const sub = UserStore.getUserSubjectsDetails?.currentSubjects[i]
                if ( !uniqueSubjects[sub.subject_id] && facultyMaterials.findIndex( material => String( material.subject_id ) === String( sub.subject_id ) ) < 0 ) {
                    availableSubjects.push( sub )
                    uniqueSubjects[sub.subject_id] = true
                }
            }
            setAvailableSubjects( availableSubjects )
        }
        setIsLoadingSubjects( false )
    }, [UserStore, StudyMaterialStore] )

    useEffect( () => {
        getSubjects()
    }, [getSubjects] )

    return (
        <Box padding="20px" overflow="auto" flexGrow={1}>
            <BreadcrumbList items={[{ link: UserStore.getUser['user_role'] === "STUDENT" ? '/student/material' : '/faculty/material', label: "Study materials" }, "Add new"]} />
            <Box>
                <Box >
                    <Typography variant="h1" fontWeight="600" component="h1" fontSize="16px">Add new material</Typography>
                    <Typography variant="subtitle2" fontSize="12px" color='textSecondary' >Provide all the required information to create a material.</Typography>
                </Box>
                <Box marginTop="20px" borderRadius="5px" >
                    <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={addMaterial}>
                        {( { setFieldValue, resetForm, values } ) => (
                            <Form>
                                <Box display="flex" sx={{ borderRadius: "5px", background: palette.errorMessage.main, padding: "10px", marginBottom: "20px", alignItems: "flex-start" }}>
                                    <InfoIcon sx={{ marginRight: "5px", marginTop: "5px", color: "white", fontSize: "20px" }} />
                                    <Box padding="5px 0" display="flex" gap="10px" flexDirection="column">
                                        <Typography variant="subtitle2" color="white" sx={{ fontWeight: "bold" }}>
                                            Note: {SettingsStore.getUseSettings['sample_study_material_format_note'] || "The institute has recommended a specific format for creating and uploading Study material, we recommend to do the same"}
                                        </Typography>
                                        {SettingsStore.getUseSettings['sample_study_material_format'] && Array.isArray( JSON.parse( SettingsStore.getUseSettings['sample_study_material_format'] ) ) && JSON.parse( SettingsStore.getUseSettings['sample_study_material_format'] ).length > 0 && (
                                            <Box display="flex" gap="5px" alignItems="center" flexWrap="wrap">
                                                {JSON.parse( SettingsStore.getUseSettings['sample_study_material_format'] ).slice( 0, 2 ).map( ( file, index ) => (
                                                    <Box borderRadius="20px" bgcolor={palette.common.white} key={index} gap="10px" padding='5px' sx={{ display: 'flex', alignItems: 'center' }}>
                                                        <Box component="img" sx={{ objectFit: "cover" }} width="35px" src={attachmentThumbnails[file.name?.split( "." ).pop()] ? attachmentThumbnails[file.name?.split( "." ).pop()] : attachmentThumbnails["default"]} alt={file.type} />
                                                        <Box sx={{ flexGrow: 1 }}>
                                                            <Typography variant="subtitle2" component="p" color="black" fontSize="12px">{file.name}</Typography>
                                                            <Typography variant="subtitle2" color="black" display="block" marginTop="-5px" fontSize="10px">
                                                                <em>{getFileSizeFromBytes( file.size )}</em>
                                                            </Typography>
                                                        </Box>
                                                        <IconButton color="black" sx={{ color: "black" }} size='small' onClick={() => handleViewFile( file )}>
                                                            <Visibility sx={{ fontSize: "18px" }} />
                                                        </IconButton>
                                                        <IconButton color="black" sx={{ color: "black" }} size='small' onClick={() => handleDownloadFile( file )}>
                                                            <Download sx={{ fontSize: "18px" }} />
                                                        </IconButton>
                                                    </Box>
                                                ) )}
                                                {JSON.parse( SettingsStore.getUseSettings['sample_study_material_format'] ).length > 2 && <Button endIcon={Icons.default.KeyboardArrowDownIcon} sx={{ width: "fit-content", color: "white", fontWeight: "400", textTransform: "capitalize" }}
                                                    onClick={handleOpenPopover}>
                                                    View more
                                                </Button>}
                                            </Box>
                                        )}
                                    </Box>
                                </Box>
                                <FormControl fullWidth >
                                    <Typography htmlFor="name" variant="subtitle2" display="block" color={palette.form.labelColor} component="label" gutterBottom>Material name *</Typography>
                                    <Field as={TextField} size="small" placeholder="Eg: New material for DS" id="name" InputProps={{ sx: { fontSize: "14px" } }} fullWidth type="text" name="materialName" />
                                    <FormHelperText > <ErrorMessage name="materialName" /> </FormHelperText>
                                </FormControl>
                                <FormControl fullWidth margin="normal" >

                                    <Typography htmlFor="desc" variant="subtitle2" display="block" color={palette.form.labelColor} component="label" gutterBottom>Description</Typography>
                                    <Field size="small" placeholder="Description goes here..." id="desc" as={TextField} InputProps={{ sx: { fontSize: "14px" } }} multiline rows={5} fullWidth type="text" name="materialDesc" />
                                    <FormHelperText > <ErrorMessage name="materialDesc" /> </FormHelperText>
                                </FormControl>

                                <FormControl id="FormControlForAcYear" fullWidth margin='normal'>
                                    <Box justifyContent="space-between" display="flex" gap="10px" alignItems="flex-end">
                                        <Typography component="label" htmlFor='academicYearInput' gutterBottom variant='subtitle2' color={palette.form.labelColor}>
                                            Select academic year *
                                        </Typography>
                                        <Button id="academicYearRefreshBtn" disabled={refreshing === "years"} startIcon={refreshing === "years" ? <CircularProgress size={14} /> : Icons.default.Sync} onClick={refreshYears} sx={{ width: "fit-content", flexShrink: 0, fontSize: "12px" }} color="secondary" >
                                            {refreshing === "years" ? "Refreshing academic years..." : "Refresh academic years"}
                                        </Button>
                                    </Box>
                                    <CustomSelectField itemValueKey={"ac_year_id"} renderOptionText={item => item.ac_year} data={UserStore.getUserAcademicYears} placeholder='Academic Year' size="small" onChange={event => handleAcademicYearChange( event, setFieldValue )} value={values.academicYear} />
                                </FormControl>

                                <FormControl fullWidth margin="normal">
                                    <Typography htmlFor="subject" variant="subtitle2" display="block" color={palette.form.labelColor} component="label" gutterBottom>Select a material subject from academic year {UserStore.getUserAcademicYears.find( ac => ac.ac_year_id === values.academicYear )?.ac_year} *</Typography>
                                    <CustomSelectField
                                        id="subjectSelector"
                                        size="small"
                                        renderValue={selected => {
                                            if ( selected ) {
                                                const subject = UserStore.getUserSubjectsDetails?.currentSubjects.find( sub => +sub.subject_id === +selected )
                                                if ( subject )
                                                    return `${subject.subject_name?.toLowerCase()} (${subject.subject_code})`
                                                else
                                                    return "Subject"
                                            } else {
                                                return "Subject"
                                            }
                                        }}
                                        itemValueKey="subject_id"
                                        itemIdentityKey="unique_subject_key"
                                        data={( availableSubjects || [] ).filter( sub => subjectFilterFunc( sub, values ) )}
                                        disabled={!values.academicYear}
                                        sx={{ color: !values.subject && palette.form.placeholder, textTransform: "capitalize" }}
                                        value={isLoadingSubjects ? "" : values?.subject || ""}
                                        onChange={e => setFieldValue( 'subject', e.target.value )}
                                        name="subject"
                                        renderOptionText={( item, index ) => <Box id={`subject-option-${index}`} display="flex" flexDirection="column">
                                            <Typography textTransform="capitalize" variant='subtitle2'>
                                                {item.subject_name?.toLowerCase()}
                                            </Typography>
                                            <Box display="flex" gap="5px" alignItems="center">
                                                <CustomChip tooltipTitle='Subject code' value={item.subject_code} />
                                                <CustomChip tooltipTitle='Year' value={`Year: ${item.year}`} />
                                                {item.sem && <CustomChip tooltipTitle='Semester' value={`Sem: ${item.sem}`} />}
                                            </Box>
                                        </Box>}
                                    />
                                    {UserStore.getErrorsStatuses?.subjects && <Box bgcolor={palette.errorMessage.light + "22"} marginTop="10px" borderRadius="5px" padding="5px 10px" border={`1px dotted ${palette.errorMessage.main}`} display="flex" alignItems="center" gap="5px" fontSize="12px" color="textSecondary">
                                        <Typography color="errorMessage.main" variant='subtitle2'>Failed to fetch <strong><em>subjects</em></strong> from ERP...</Typography>
                                        <Button color="errorMessage" sx={{ textTransform: "capitalize" }} onClick={() => getSubjects()} endIcon={isLoadingSubjects ? <CircularProgress size={14} /> : Icons.default.Replay}>Retry</Button>
                                    </Box>}
                                    {isLoadingSubjects && !UserStore.getErrorsStatuses?.subjects && <Typography marginTop="5px" display="flex" alignItems="center" gap="5px" fontSize="12px" color="textSecondary"> <DataLoadingSpinner padding='0' size={14} /> {`Fetching your subjects from ERP...`} </Typography>}

                                    <Typography marginTop="5px" padding="5px 10px" borderRadius="5px" bgcolor={palette.contentBg} border={border[1]} display="flex" gap="10px" alignItems="center" fontSize="18px">
                                        {Icons.default.Info}
                                        <Typography component="span" fontSize="12px" >If subject not displayed in the dropdown then you already have a material for that subject</Typography>
                                    </Typography>
                                    <FormHelperText > <ErrorMessage name="subject" /> </FormHelperText>
                                </FormControl>
                                <FormControl fullWidth>
                                    <Typography htmlFor="tags" variant="subtitle2" display="block" color={palette.form.labelColor} component="label" gutterBottom>Add Tags *{`(Type tags as comma seperated values)`}</Typography>
                                    <Box >
                                        <TextField id="tags" size="small" type="text" placeholder='Add new tag' fullWidth onBlur={( e ) => { handleBlur( e, setFieldValue ) }} onKeyDown={e => { handleKeyDown( e, setFieldValue ) }} slotProps={{
                                            input: { sx: { fontSize: "14px" } }
                                        }} />
                                        <FormHelperText > <ErrorMessage name="tags" /> </FormHelperText>
                                        <Collapse in={tags.length > 0}>
                                            <Box marginTop="5px" display="flex" alignItems="center" gap="5px">
                                                {tags.length > 0 && tags.map( ( tag, index ) => {
                                                    return (
                                                        <Chip deleteIcon={Icons.default.CloseIcon} key={index} sx={{ borderRadius: "5px", border: border[1], background: palette.contentBg }} label={tag} onDelete={() => deleteTag( index, setFieldValue )} size="medium" />
                                                    )
                                                } )}
                                            </Box>
                                        </Collapse>
                                    </Box>
                                </FormControl>
                                <Box marginTop="20px">
                                    <Typography variant="subtitle2" display="block" color={palette.form.labelColor} component="label" gutterBottom>Select study material resources (Optional; Can be uploaded after the study material is created.)</Typography>
                                    <FileInput attachmentsSizes={attachmentsSizes} setAttachmentsSizes={setAttachmentsSizes} progress={progress} size={60} isUploading={isAddingMaterial} selectedAttachments={selectedAttachments} setSelectedAttachments={setSelectedAttachments} setFieldValue={setFieldValue} attachmentsNames={attachmentsNames} setAttachmentsNames={setAttachmentsNames} />
                                </Box>
                                <Box marginTop="60px" alignItems="center" gap="20px" display="flex">
                                    <Button disabled={isAddingMaterial} type="submit" variant='contained' startIcon={isAddingMaterial ? <CircularProgress size={20} /> : Icons.default.AddIcon} sx={{ textTransform: "capitalize" }} disableElevation>{isAddingMaterial ? "Adding Material please wait..." : `Add new material`}</Button>
                                    <Button disableElevation variant='contained' onClick={() => navigate( '/faculty/material' )} color='error' sx={{ textTransform: "capitalize" }}>Cancel</Button>
                                </Box>
                            </Form>
                        )}
                    </Formik>
                </Box>
            </Box>
            <Popover open={Boolean( anchorEl )} anchorEl={anchorEl} onClose={handleClosePopover} anchorOrigin={{ vertical: 'bottom', horizontal: 'left', }} transformOrigin={{ vertical: 'top', horizontal: 'left', }}>
                <Box sx={{ maxWidth: 400 }}>
                    {formatFiles.slice( 2 ).map( ( file, index ) => (
                        <Box borderBottom={border[1]} key={index} gap="10px" padding='5px' sx={{ display: 'flex', alignItems: 'center' }}>
                            <img style={{ objectFit: "cover" }} width="45px" src={attachmentThumbnails[file.name?.split( "." ).pop()] ? attachmentThumbnails[file.name?.split( "." ).pop()] : attachmentThumbnails["default"]} alt={file.type} />
                            <Box sx={{ flexGrow: 1 }}>
                                <Typography variant="subtitle2" fontSize="12px">{file.name}</Typography>
                                <Typography variant="subtitle2" fontSize="10px" color="text.secondary">
                                    {getFileSizeFromBytes( file.size )}
                                </Typography>
                            </Box>
                            <IconButton onClick={() => handleViewFile( file )}>
                                <Visibility />
                            </IconButton>
                            <IconButton onClick={() => handleDownloadFile( file )}>
                                <Download />
                            </IconButton>
                        </Box>
                    ) )}
                </Box>
            </Popover>
            <Drawer anchor='bottom' sx={{ zIndex: 9000000 }} open={isDialogOpen} slotProps={{
                paper: { sx: { height: "calc(100% - 50px)" } }
            }}>
                <Box bgcolor={palette.contentBg} flexGrow={1} display="flex" overflow="auto" flexDirection="column" >
                    <Box borderBottom={1} alignItems="center" display="flex" justifyContent="space-between" gap="20px" padding="10px 20px">
                        <Typography variant="h1" fontWeight="600" component="h1" fontSize="16px">{filename}</Typography>
                        <Box display="flex" gap="10px" alignItems="center">
                            <IconButton onClick={handleCloseDialog}>
                                {Icons.default.CloseIcon}
                            </IconButton>
                        </Box>
                    </Box>
                    <Box display="flex" flexDirection="column" overflow="auto" flexGrow={1} padding="20px" height="300px">
                        <DocumentViewer type={documentType} loadingText="Loading material..." file={documentUrl} />
                    </Box>
                </Box>
            </Drawer>
        </Box>
    )
}

export default AddMaterial